// AUTOMATIKUS VÁZLAT-MENTÉS egy beviteli helyhez (jegy-válasz, üzenet) — a tároló-szabályok a `lib/drafts.ts`-ben.
//
// A HASZNÁLAT: a komponens a `restore()`-ral tölti vissza a vázlatot (megnyitáskor / hely-váltáskor), minden
// változásnál `save(value, isEmpty)`-t hív (a mentés késleltetett — gépelés közben nem ír minden billentyűre), a
// sikeres küldés és a „Vázlat elvetése" után `discard()`-ot. A `savedAt` a jelzés forrása („Vázlat mentve · 12:03").
//
// KIKAPCSOLT (nem ment, nem tölt vissza):
//   • amíg a munkamenet nem ismert (nincs `sub` a kulcshoz),
//   • MEGSZEMÉLYESÍTÉS alatt — a támogató munkatárs szövege nem kerülhet a cél-felhasználó vázlatai közé,
//   • ha a hívó `scope`-ja `null`.
import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '@/lib/api';
import { draftGeneration, draftKey, readDraft, removeDraft, writeDraft } from '@/lib/drafts';

/** A gépelés utáni késleltetés, mielőtt a vázlat mentődik. */
export const DRAFT_SAVE_DELAY_MS = 600;

export interface DraftHandle<T> {
  /** Működik-e (van munkamenet, nincs megszemélyesítés, van hely). */
  enabled: boolean;
  /** A tárolt vázlat értéke (vagy `null`). */
  restore: () => T | null;
  /** Késleltetett mentés; üres értéknél a vázlat törlődik (nincs „üres vázlat"). */
  save: (value: T, isEmpty: boolean) => void;
  /** A vázlat azonnali törlése (küldés után / „Vázlat elvetése"). */
  discard: () => void;
  /** Az utolsó mentés ideje (a jelzéshez) — `null`, ha nincs mentett vázlat. */
  savedAt: Date | null;
}

export interface DraftOptions<T> {
  /**
   * HELY-váltáskor (jegy / fajta / megnyitás) a hely vázlatával hívódik — ide írja a hívó a szerkesztőbe. A munkamenet
   * KÉSŐI betöltődésekor (a kulcs `null`-ról lesz érvényes) vázlat nélkül NEM hívódik: a már begépelt szöveget
   * üresre írná. Valódi hely-váltáskor viszont `null`-lal is hívódik (az új helyen nincs vázlat → üres szerkesztő).
   */
  onRestore?: (value: T | null) => void;
}

export function useDraft<T>(scope: string | null, options: DraftOptions<T> = {}): DraftHandle<T> {
  const sessionQ = api.session.useQuery(['session'], {});
  const session = sessionQ.data?.status === 200 ? sessionQ.data.body : null;
  const key =
    session && !session.impersonation && scope !== null ? draftKey(session.sub, scope) : null;

  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<(() => void) | null>(null);
  // a még ki nem írt (függő) érték — a gyors újranyitás ezt olvassa, ne a tárolt régit
  const latest = useRef<{ key: string; value: T; isEmpty: boolean } | null>(null);

  // a legutóbbi callback (a hívó minden renderben újat adhat — a hely-váltás effektje ne függjön tőle)
  const onRestore = useRef(options.onRestore);
  onRestore.current = options.onRestore;
  const prevKey = useRef<string | null>(null);

  // hely-váltáskor a jelzés és a szerkesztő az ÚJ hely vázlatáé
  useEffect(() => {
    if (key === null) {
      setSavedAt(null);
      // a kulcs (átmeneti) kiesése után a visszatérés „késői betöltésnek" számít: nem írja felül a szöveget
      prevKey.current = null;
      return;
    }
    const d = readDraft<T>(key);
    setSavedAt(d ? new Date(d.savedAt) : null);
    const cameFromKnownPlace = prevKey.current !== null;
    prevKey.current = key;
    if (d !== null || cameFromKnownPlace) onRestore.current?.(d?.value ?? null);
  }, [key]);

  // a függő (még ki nem írt) mentés NEM vész el hely-váltáskor / lecsatoláskor — azonnal kiírjuk
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      pending.current?.();
      pending.current = null;
    },
    [key],
  );

  const restore = useCallback((): T | null => {
    if (!key) return null;
    const l = latest.current;
    if (l?.key === key) return l.isEmpty ? null : l.value;
    return readDraft<T>(key)?.value ?? null;
  }, [key]);

  const save = useCallback(
    (value: T, isEmpty: boolean) => {
      if (!key) return;
      const gen = draftGeneration();
      latest.current = { key, value, isEmpty };
      const write = (): void => {
        pending.current = null;
        if (latest.current?.key === key) latest.current = null;
        // a közben lefutott kijelentkezés (generáció-váltás) után a függő szöveg NEM kerülhet vissza a gépre
        if (gen !== draftGeneration()) return;
        if (isEmpty) {
          removeDraft(key);
          setSavedAt(null);
          return;
        }
        const now = Date.now();
        if (writeDraft(key, value, now)) setSavedAt(new Date(now));
      };
      if (timer.current) clearTimeout(timer.current);
      pending.current = write;
      timer.current = setTimeout(write, DRAFT_SAVE_DELAY_MS);
    },
    [key],
  );

  const discard = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    pending.current = null;
    latest.current = null;
    if (key) removeDraft(key);
    setSavedAt(null);
  }, [key]);

  return { enabled: key !== null, restore, save, discard, savedAt };
}
